import { FC, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Navigate } from 'react-router';

import { authSelector, resetHasLoggedIn } from 'store/authState';
import {
  checkProductRegistration,
  productRegistrationSelector
} from 'store/productRegistrationState';
import { localizationSelector } from 'store/localizationState';
import { displayError } from 'store/globalErrorState';
import { loginRedirect } from 'services/authService';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { useTranslations } from 'hooks/useTranslations';
import { useQuery } from 'hooks/useQuery';
import { pushGTMEvent } from 'utils/analyticsUtils';
import * as routes from 'router/routes';
import { isConstruction } from 'config';

import Loader from 'components/UI/Loader';
import Heading from 'components/UI/Heading';
import Button from 'components/UI/Buttons/Button';
import PageImageWrapper from 'components/UI/Page/PageImageWrapper';
import SeoHelmet from 'components/fragments/SeoHelmet';

import { ForgotPasswordContainer, LoginContainer } from './styled';

const LoginPage: FC = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { productInfo, shouldComplete } = useAppSelector(
    productRegistrationSelector
  );
  const {
    session: { authenticated },
    isLoading
  } = useAppSelector(authSelector);
  const { languageCode = 'en' } = useAppSelector(localizationSelector);

  // Params
  const [error] = useQuery('error');

  // Reset has logged in state and check product registration
  useEffect(() => {
    dispatch(resetHasLoggedIn());
    dispatch(checkProductRegistration());
  }, [dispatch]);

  // Handle DIAM error
  useEffect(() => {
    if (error) {
      dispatch(displayError('login.error'));
    }
  }, [dispatch, error]);

  // Actions
  const onLogin = useCallback(() => {
    pushGTMEvent('loginClick');
    loginRedirect(languageCode);
  }, [languageCode]);
  const onSignUp = useCallback(() => {
    pushGTMEvent('signupClick');
    navigate(routes.SIGNUP);
  }, [navigate]);
  const onForgotPassword = useCallback(
    () => navigate(routes.RESET_PASSWORD),
    [navigate]
  );

  // Redirect if authenticated
  const redirectPath = useMemo(() => {
    if (productInfo?.husqvarnaId) {
      return `${routes.PRODUCT_INFO}?husqvarnaId=${productInfo.husqvarnaId}`;
    }
    if (productInfo?.pnc && productInfo?.serialNumber) {
      return `${routes.PRODUCT_INFO}?productno=${productInfo.pnc}&serialNumber=${productInfo.serialNumber}`;
    }
    return routes.PRODUCT_REG;
  }, [productInfo]);

  // Register invites
  if (authenticated && productInfo && shouldComplete) {
    return <Navigate to={redirectPath} />;
  }

  // Redirect to products
  if (authenticated) {
    return <Navigate to={routes.PRODUCTS} />;
  }

  // Loading
  if (isLoading) {
    return <Loader center padding size="large" />;
  }

  return (
    <PageImageWrapper>
      <SeoHelmet title="login.meta_title" />
      <Heading>
        {t(`login.${isConstruction ? 'construction' : 'husqvarna'}.title`)}
      </Heading>
      <p>
        {t(
          `login.${isConstruction ? 'construction' : 'husqvarna'}.description`
        )}
      </p>
      <LoginContainer>
        <Button onClick={onLogin} className="loginButton">
          {t('login.btn_login')}
        </Button>
        <Button
          onClick={onSignUp}
          color="background"
          textColor="primary"
          border
        >
          {t('login.btn_signup')}
        </Button>
        <ForgotPasswordContainer>
          <Button
            onClick={onForgotPassword}
            color="transparent"
            textColor={isConstruction ? 'grey4' : 'primary'}
          >
            {t('login.btn_forgot_password')}
          </Button>
        </ForgotPasswordContainer>
      </LoginContainer>
    </PageImageWrapper>
  );
};

export default LoginPage;
